import {Box, Card} from '@mui/material';
import {type KeyboardEvent, type ReactNode, useEffect, useRef, useState} from 'react';

/** タブ下のメインエリアの高さ（ページにスクロールバーを出さない） */
export const MAIN_AREA_HEIGHT = 'calc(100vh - 215px)';

const MIN_LEFT_PANEL_WIDTH = 150;
const MIN_VIEWER_WIDTH = 360;
const RESIZE_HANDLE_TOTAL_WIDTH = 20;
const PANEL_RESIZE_STEP = 24;
const DEFAULT_LEFT_PANEL_WIDTH = 300;

const maxLeftWidth = (containerWidth: number) =>
  Math.max(MIN_LEFT_PANEL_WIDTH, containerWidth - RESIZE_HANDLE_TOTAL_WIDTH - MIN_VIEWER_WIDTH);

/** 左: カード（ファイルツリー等）、右: 内容 の左右分割。境界はドラッグ/矢印キーで幅を変えられる */
export default function SplitPane({left, children}: {left: ReactNode; children: ReactNode}) {
  const [leftPanelWidth, setLeftPanelWidth] = useState(DEFAULT_LEFT_PANEL_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setLeftPanelWidth(
        Math.max(MIN_LEFT_PANEL_WIDTH, Math.min(e.clientX - rect.left, maxLeftWidth(rect.width)))
      );
    };
    const handleMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // コンテナが狭くなったら右側の最小幅を確保する（非表示中の幅 0 は無視）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0) setLeftPanelWidth(current => Math.min(current, maxLeftWidth(width)));
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const handleMouseDown = () => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? PANEL_RESIZE_STEP : -PANEL_RESIZE_STEP;
    const width = containerRef.current?.getBoundingClientRect().width ?? 0;
    setLeftPanelWidth(current =>
      Math.max(MIN_LEFT_PANEL_WIDTH, Math.min(current + delta, maxLeftWidth(width)))
    );
  };

  return (
    <Box ref={containerRef} sx={{display: 'flex', height: '100%', overflow: 'hidden'}}>
      <Box sx={{width: leftPanelWidth, flexShrink: 0, height: '100%', overflow: 'hidden'}}>
        <Card
          variant="outlined"
          sx={{p: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}
        >
          {left}
        </Card>
      </Box>

      {/* リサイズハンドル */}
      <Box
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        role="separator"
        aria-orientation="vertical"
        tabIndex={0}
        sx={{
          width: 12,
          mx: 0.5,
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover > div': {bgcolor: 'primary.main'}
        }}
      >
        <Box sx={{width: 4, height: 40, bgcolor: 'divider', borderRadius: 1}} />
      </Box>

      <Box sx={{flex: 1, minWidth: MIN_VIEWER_WIDTH, overflow: 'hidden'}}>{children}</Box>
    </Box>
  );
}
